import { useMemo } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PurchaseLineResultDto } from '@siliconx/shared';

/**
 * SIL-1073 — margin-vs-hold-month curve, the builder's centerpiece (design
 * spec §4 "The curve is the centerpiece"): "The main chart plots margin
 * against hold month 0 to 12, with the p10/p90 band drawn at the chosen
 * month only, never as two separate paths. The winning month is a marker on
 * that curve."
 *
 * `bandAtBestMonth` is a RESALE PRICE band (lo/med/hi), not a margin band —
 * the engine never computes a margin band because `revenue(m)` is linear in
 * price and `perUnitCost(m)` does not depend on price (packages/shared/src/
 * purchase-engine.ts `marginAtMonth`). That means the margin equivalent of
 * the price band is derivable exactly from fields already on the DTO,
 * without the client re-deriving cost rows (never sent to the client):
 *
 *   ratio = atBestMonth.revenueUsd / bandAtBestMonth.medUsd    (= 1 - pctRevenue)
 *   marginAt(price) = ratio * price - atBestMonth.perUnitCostUsd
 *
 * so marginLo/marginHi at the chosen month follow from the same ratio
 * applied to loUsd/hiUsd. This is exact, not an approximation — see
 * `__tests__/PurchaseMarginCurveChart.test.tsx` "band-to-margin conversion
 * is exact" for the round-trip proof against `marginAtMonth`.
 */

const LINE_COLOR = '#047857'; // emerald-700, matches OutlookBand's forecast series
const WINNER_COLOR = '#b45309'; // amber-700 — the winning-month marker
const BAND_COLOR = '#b45309';
const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';

interface ChartPoint {
  month: number;
  marginUsd: number;
  /** [low, high] margin error relative to marginUsd, only set at bestMonth. */
  error?: [number, number];
}

export function computeMarginBandError(
  result: Pick<PurchaseLineResultDto, 'atBestMonth' | 'bandAtBestMonth'>,
): [number, number] | undefined {
  const band = result.bandAtBestMonth;
  if (!band || band.medUsd === null || band.loUsd === null || band.hiUsd === null || band.medUsd === 0) {
    return undefined;
  }
  const ratio = result.atBestMonth.revenueUsd / band.medUsd;
  const marginLo = result.atBestMonth.marginUsd + ratio * (band.loUsd - band.medUsd);
  const marginHi = result.atBestMonth.marginUsd + ratio * (band.hiUsd - band.medUsd);
  const errLow = result.atBestMonth.marginUsd - marginLo;
  const errHigh = marginHi - result.atBestMonth.marginUsd;
  if (errLow === 0 && errHigh === 0) return undefined;
  return [errLow, errHigh];
}

export function buildCurveChartData(result: PurchaseLineResultDto): ChartPoint[] {
  const error = computeMarginBandError(result);
  return result.curve.map((p) => ({
    month: p.month,
    marginUsd: p.marginUsd,
    error: p.month === result.bestMonth ? error : undefined,
  }));
}

function fmtUsdShort(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function PurchaseMarginCurveChart({ result }: { result: PurchaseLineResultDto }) {
  const data = useMemo(() => buildCurveChartData(result), [result]);
  const winner = data.find((d) => d.month === result.bestMonth);

  if (data.length <= 1) {
    return (
      <p className="text-xs text-muted-foreground">
        Sell-now only — no forecast curve to plot for this line.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <div className="min-w-[360px]">
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={data} margin={{ top: 14, right: 12, bottom: 0, left: 4 }}>
            <CartesianGrid stroke={BORDER} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: MUTED }}
              tickLine={false}
              axisLine={{ stroke: BORDER }}
              tickFormatter={(m: number) => `M${m}`}
            />
            <YAxis
              tick={{ fontSize: 12, fill: MUTED }}
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={(v: number) => fmtUsdShort(v)}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 6,
                borderColor: BORDER,
                backgroundColor: 'hsl(var(--popover, 0 0% 100%))',
                color: 'hsl(var(--popover-foreground, 240 10% 4%))',
              }}
              labelFormatter={(m: number) => `Hold month ${m}`}
              formatter={(value: number) => [fmtUsdShort(value), 'Margin']}
            />
            <Line
              dataKey="marginUsd"
              name="Margin"
              stroke={LINE_COLOR}
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            >
              <ErrorBar dataKey="error" width={4} strokeWidth={2} stroke={BAND_COLOR} direction="y" />
            </Line>
            {winner && (
              <ReferenceDot
                x={winner.month}
                y={winner.marginUsd}
                r={5}
                fill={WINNER_COLOR}
                stroke="white"
                label={{ value: 'Best', position: 'top', fontSize: 12, fill: WINNER_COLOR }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {result.bandAtBestMonth && (
        <p className="text-xs text-muted-foreground">
          Band at month {result.bestMonth}: {fmtUsdShort(result.bandAtBestMonth.loUsd ?? 0)} –{' '}
          {fmtUsdShort(result.bandAtBestMonth.hiUsd ?? 0)} resale price (median{' '}
          {fmtUsdShort(result.bandAtBestMonth.medUsd ?? 0)}).
        </p>
      )}
    </div>
  );
}
